import { useEffect, useState } from "react";
import { useApi } from "@/lib/useApi";
import { api, type FacetEntry, type Facets, type Scheme, type SearchResult } from "@/lib/api";
import { BenefitChip, Chip, Highlight, LevelChip, SourceChip } from "@/lib/chips";

export interface BrowseState {
  q: string;
  level: string;
  state: string;
  audience: string;
  benefit_type: string;
  source: string;
  offset: number;
  limit: number;
}

interface Props {
  state: BrowseState;
  onChange: (patch: Partial<BrowseState>) => void;
  onOpenScheme: (id: string) => void;
}

export function Browse({ state, onChange, onOpenScheme }: Props) {
  const { data: facets, reload: reloadFacets } = useApi(() => api.filters(), []);
  const [result, setResult] = useState<SearchResult | null>(null);
  const [status, setStatus] = useState<string>("");

  useEffect(() => {
    let active = true;
    setStatus("Searching…");
    api.search({ ...state })
      .then((r) => { if (active) { setResult(r); setStatus(""); } })
      .catch((e) => { if (active) setStatus(`Error: ${e instanceof Error ? e.message : e}`); });
    return () => { active = false; };
  }, [state.q, state.level, state.state, state.audience, state.benefit_type, state.source, state.offset, state.limit]);

  const total  = result?.total ?? 0;
  const shown  = Math.min(state.offset + state.limit, total);
  const from   = total === 0 ? 0 : state.offset + 1;
  const pages  = Math.ceil(total / state.limit);
  const curPg  = Math.floor(state.offset / state.limit) + 1;

  const clearAll = () => {
    onChange({ q: "", level: "", state: "", audience: "", benefit_type: "", source: "", offset: 0 });
    reloadFacets();
  };

  return (
    <section id="browse" className="max-w-6xl mx-auto px-5 py-20">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
        <div>
          <p className="section-eyebrow mb-2">chapter · five</p>
          <h2 className="section-title">
            Browse <span className="font-serif italic text-amber-300">everything</span>.
          </h2>
          <p className="text-cream-100/60 mt-2 max-w-xl text-sm">
            Schemes, portals, incubators — full index with live filters.
          </p>
        </div>
        <div className="text-xs font-mono uppercase tracking-[0.25em] text-cream-100/50">
          {status ? (
            status
          ) : (
            <>
              {total.toLocaleString("en-IN")} records
              {state.q ? <> for <strong>{state.q}</strong></> : null}
              {total ? ` · showing ${from}–${shown}` : ""}
            </>
          )}
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-6">
        <aside className="md:w-60 shrink-0">
          <div className="sticky top-24 space-y-5 rounded-2xl border border-amber-400/20 bg-plum-900/40 p-5 backdrop-blur-sm">
            <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-amber-300/80">
              · indices ·
            </div>
            <Facet name="audience"     label="Audience"      facets={facets} state={state} onChange={onChange} />
            <Facet name="benefit_type" label="Benefit"       facets={facets} state={state} onChange={onChange} />
            <Facet name="level"        label="Level"         facets={facets} state={state} onChange={onChange} />
            <Facet name="source"       label="Source"        facets={facets} state={state} onChange={onChange} />
            <Facet name="state"        label="State"         facets={facets} state={state} onChange={onChange}
                   scroll />
            <button onClick={clearAll}
                    className="w-full text-[11px] font-mono uppercase tracking-[0.25em] text-amber-300/70 hover:text-amber-300 py-1 border-t border-amber-400/20 pt-3">
              clear all filters
            </button>
          </div>
        </aside>

        <div className="flex-1 min-w-0">
          <div className="grid sm:grid-cols-2 gap-3.5">
            {result?.items.map((s) => <Card key={s.id} scheme={s} q={state.q} onOpen={onOpenScheme} />)}
            {result && !result.items.length && (
              <div className="col-span-full rounded-xl border border-dashed border-amber-400/30 bg-plum-900/30 p-10 text-center">
                <p className="font-display text-lg text-cream-50">No records filed.</p>
                <p className="mt-1 text-[13px] text-cream-100/55">Try broader keywords or clear filters.</p>
              </div>
            )}
          </div>

          {pages > 1 && (
            <div className="mt-10 flex items-center justify-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-cream-100/50">
              <PagerBtn
                disabled={curPg === 1}
                onClick={() => onChange({ offset: state.offset - state.limit })}
              >‹ prev folio</PagerBtn>
              <span>folio {curPg} of {pages}</span>
              <PagerBtn
                disabled={curPg === pages}
                onClick={() => onChange({ offset: state.offset + state.limit })}
              >next folio ›</PagerBtn>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

const PRETTY: Record<string, Record<string, string>> = {
  level:        { central: "Central", state: "State" },
  benefit_type: { loan: "Loan", subsidy: "Subsidy / Grant", equity: "Equity", composite: "Composite", "in-kind": "In-kind", other: "Other" },
  audience:     { startup: "Startup", msme: "MSME", "small-biz": "Small business", individual: "Individual", student: "Student" },
  source:       { myscheme: "MyScheme", startupindia: "Startup India", msme: "MSME", mudra: "Mudra", ecosystem: "Incubator / Portal", niti: "NITI Aayog", meity: "MeitY", dpiit: "DPIIT", dst: "DST", nabard: "NABARD", standupindia: "Stand-Up India", state_missions: "State portals", x_feed: "Announcements", google_grants: "Discovered" },
  state:        {},
};

function Facet({ name, label, facets, state, onChange, scroll }: {
  name: keyof BrowseState;
  label: string;
  facets: Facets | null;
  state: BrowseState;
  onChange: (patch: Partial<BrowseState>) => void;
  scroll?: boolean;
}) {
  const entries: FacetEntry[] = facets ? ((facets as any)[name] as FacetEntry[]) : [];
  return (
    <div>
      <h3 className="font-display text-[11px] font-semibold uppercase tracking-[0.22em] text-cream-50/80 mb-2.5 pb-1 border-b border-amber-400/15">
        {label}
      </h3>
      <div className={`space-y-1 ${scroll ? "max-h-52 overflow-auto pr-1" : ""}`}>
        {entries.slice(0, 50).map((e) => {
          const active = (state as any)[name] === e.value;
          const pretty = PRETTY[name]?.[e.value] ?? e.value;
          return (
            <label key={e.value}
                   className={`flex items-center gap-2 text-[13px] cursor-pointer ${
                     active ? "font-semibold text-amber-300" : "text-cream-100/75 hover:text-cream-50"
                   }`}>
              <input
                type="radio" name={`facet-${name}`} checked={active}
                onChange={() =>
                  onChange({ [name]: (active ? "" : e.value), offset: 0 } as Partial<BrowseState>)
                }
                className="accent-amber-400"
              />
              <span className="flex-1 truncate">{pretty}</span>
              <span className="text-[11px] font-mono text-cream-100/40">{e.count}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function Card({ scheme: s, q, onOpen }: { scheme: Scheme; q: string; onOpen: (id: string) => void }) {
  const metaBits = [s.ministry, s.state].filter(Boolean).join(" · ");
  const trimmed = s.summary
    ? (s.summary.length > 260 ? s.summary.slice(0, 260) + "…" : s.summary)
    : "";
  return (
    <article
      onClick={() => onOpen(s.id)}
      className="group relative rounded-xl border border-white/8 bg-plum-900/40 p-4 cursor-pointer flex flex-col gap-2
                 hover:border-amber-400/35 hover:bg-plum-900/70 transition-colors"
    >
      <div className="flex flex-wrap gap-1">
        <LevelChip level={s.level} />
        <BenefitChip kind={s.benefit_type} />
        <SourceChip source={s.source} />
        {s.audience && (
          <Chip>{s.audience === "small-biz" ? "Small biz" : s.audience[0].toUpperCase() + s.audience.slice(1)}</Chip>
        )}
      </div>
      <div className="flex items-start justify-between gap-3">
        <h3 className="font-display font-semibold text-[15px] leading-snug min-w-0 text-cream-50 group-hover:text-amber-200 transition-colors">
          <Highlight text={s.name} query={q} />
        </h3>
        {s.short_title && (
          <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-amber-300/70 shrink-0 pt-1">
            {s.short_title}
          </span>
        )}
      </div>
      {metaBits && (
        <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-cream-100/50">{metaBits}</p>
      )}
      {trimmed && (
        <p className="text-[13px] text-cream-100/70 leading-snug">
          <Highlight text={trimmed} query={q} />
        </p>
      )}
      {s.tags && s.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-auto pt-1">
          {s.tags.slice(0, 3).map((t) => <Chip key={t}>{t}</Chip>)}
        </div>
      )}
    </article>
  );
}

function PagerBtn({ disabled, onClick, children }: {
  disabled: boolean; onClick: () => void; children: React.ReactNode;
}) {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.25em] font-mono rounded-xl border border-amber-400/25 transition ${
        disabled ? "opacity-35 cursor-not-allowed" : "hover:bg-amber-400/10 hover:text-amber-300 text-cream-100/70"
      }`}
    >
      {children}
    </button>
  );
}
